import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  Filter,
  Eye,
  MoreVertical,
  CheckCircle,
  XCircle,
  Calendar,
  FileText,
  ArrowLeft,
  Shield,
  RefreshCw,
  Users as UsersIcon
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

// Mock data - will be replaced with API calls
const mockUserStats = {
  active: 12847,
  pending: 47,
  blocked: 23
};

const mockActiveUsers = [
  {
    id: '1',
    name: 'Sarah Johnson',
    email: 'sarah.j@email.com',
    avatar: null,
    role: 'coach',
    sport: 'Tennis',
    status: 'active',
    lastActive: '2 hours ago'
  },
  {
    id: '2',
    name: 'Mike Chen',
    email: 'mike.chen@email.com',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face',
    role: 'player',
    sport: 'Football',
    status: 'pending',
    lastActive: '1 day ago'
  },
  {
    id: '3',
    name: 'Emma Davis',
    email: 'emma.davis@email.com',
    avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face',
    role: 'coach',
    sport: 'Swimming',
    status: 'active',
    lastActive: '30 minutes ago'
  },
  {
    id: '4',
    name: 'John Smith',
    email: 'john.smith@email.com',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face',
    role: 'player',
    sport: 'Chess',
    status: 'blocked',
    lastActive: '1 week ago'
  },
  {
    id: '5',
    name: 'Lisa Wang',
    email: 'lisa.wang@email.com',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=40&h=40&fit=crop&crop=face',
    role: 'player',
    sport: 'Basketball',
    status: 'active',
    lastActive: '5 minutes ago'
  },
  {
    id: '6',
    name: 'Alex Rodriguez',
    email: 'alex.rod@email.com',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=40&h=40&fit=crop&crop=face',
    role: 'coach',
    sport: 'Football',
    status: 'pending',
    lastActive: '3 days ago'
  }
];

const mockPendingUsers = [
  {
    id: '2',
    name: 'Mike Chen',
    email: 'mike.chen@email.com',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face',
    role: 'player',
    sport: 'Football',
    submittedAt: '2024-10-17',
    documentCount: 2,
    documents: ['ID Verification', 'Medical Certificate']
  },
  {
    id: '6',
    name: 'Alex Rodriguez',
    email: 'alex.rod@email.com',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=60&h=60&fit=crop&crop=face',
    role: 'coach',
    sport: 'Football',
    submittedAt: '2024-08-17',
    documentCount: 3,
    documents: ['ID Verification', 'Coaching Certificate', 'Background Check']
  },
  {
    id: '7',
    name: 'Jennifer Lee',
    email: 'jen.lee@email.com',
    avatar: null,
    role: 'coach',
    sport: 'Tennis',
    submittedAt: '2024-08-17',
    documentCount: 2,
    documents: ['ID Verification', 'Tennis Coaching License']
  },
  {
    id: '8',
    name: 'David Kim',
    email: 'david.kim@email.com',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face',
    role: 'player',
    sport: 'Swimming',
    submittedAt: '2024-07-17',
    documentCount: 3,
    documents: ['ID Verification', 'Medical Certificate', 'Swimming Club Membership']
  },
  {
    id: '9',
    name: 'Maria Santos',
    email: 'maria.santos@email.com',
    avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=60&h=60&fit=crop&crop=face',
    role: 'player',
    sport: 'Chess',
    submittedAt: '2024-06-17',
    documentCount: 2,
    documents: ['ID Verification', 'Chess Rating Certificate']
  }
];

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'active' | 'pending'>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sportFilter, setSportFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [stats, setStats] = useState(mockUserStats);
  const [activeUsers, setActiveUsers] = useState(mockActiveUsers);
  const [pendingUsers, setPendingUsers] = useState(mockPendingUsers);
  const [loading, setLoading] = useState(false);

  // Load data on component mount and when filters change
  useEffect(() => {
    // Check for search parameter from URL
    const urlSearch = searchParams.get('search');
    if (urlSearch) {
      setSearchTerm(urlSearch);
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeTab === 'active') {
      loadActiveUsers();
    } else {
      loadPendingUsers();
    }
    loadUserStats();
  }, [activeTab, searchTerm, roleFilter, statusFilter, sportFilter, sortBy]);

  // API Functions
  const loadUserStats = async () => {
    try {
      const response = await api.get('/admin/users/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load user stats, using mock data:', error);
      // Fallback to mock data
      setStats(mockUserStats);
    }
  };

  const loadActiveUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        search: searchTerm,
        role: roleFilter,
        status: statusFilter,
        sport: sportFilter,
        sort: sortBy,
        page: '1',
        limit: '50'
      });

      const response = await api.get(`/admin/users?${params}`);
      if (response.data.success) {
        setActiveUsers(response.data.data.users);
      } else {
        // If API returns success: false, use mock data
        setActiveUsers(mockActiveUsers);
      }
    } catch (error) {
      console.error('Failed to load active users, using mock data:', error);
      // Fallback to mock data on API error
      setActiveUsers(mockActiveUsers);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users/pending');
      if (response.data.success) {
        setPendingUsers(response.data.data.users);
      } else {
        // If API returns success: false, use mock data
        setPendingUsers(mockPendingUsers);
      }
    } catch (error) {
      console.error('Failed to load pending users, using mock data:', error);
      // Fallback to mock data on API error
      setPendingUsers(mockPendingUsers);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'blocked': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleBadgeColor = (role: string) => {
    return role === 'coach' ? 'bg-gray-900 text-white' : 'bg-gray-500 text-white';
  };

  const handleUserSelect = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    const currentUsers = activeTab === 'active' ? activeUsers : pendingUsers;
    if (selectedUsers.length === currentUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(currentUsers.map(user => user.id));
    }
  };

  const handleApproveUser = async (userId: string) => {
    try {
      const response = await api.post(`/admin/users/${userId}/approve`);
      if (response.data.success) {
        toast.success('User approved successfully');
        // Update local state
        setPendingUsers(prev => prev.filter(user => user.id !== userId));
        // Refresh stats from server to get accurate counts
        await loadUserStats();
        // Also refresh the active users list
        if (activeTab === 'active') {
          await loadActiveUsers();
        }
      }
    } catch (error) {
      console.error('Failed to approve user:', error);
      toast.error('Failed to approve user');
    }
  };

  const handleRejectUser = async (userId: string) => {
    try {
      const response = await api.post(`/admin/users/${userId}/reject`, {
        reason: 'Application rejected by admin'
      });
      if (response.data.success) {
        toast.success('User rejected successfully');
        // Update local state
        setPendingUsers(prev => prev.filter(user => user.id !== userId));
        // Refresh stats from server to get accurate counts
        await loadUserStats();
        setStats(prev => ({ ...prev, pending: prev.pending - 1 }));
      }
    } catch (error) {
      console.error('Failed to reject user:', error);
      toast.error('Failed to reject user');
    }
  };

  const UserAvatar: React.FC<{ user: any; size?: 'sm' | 'md' | 'lg' }> = ({ user, size = 'md' }) => {
    const sizeClasses = {
      sm: 'w-8 h-8 text-xs',
      md: 'w-10 h-10 text-sm',
      lg: 'w-16 h-16 text-lg'
    };

    return (
      <div className={`${sizeClasses[size]} rounded-full flex items-center justify-center ${
        user.avatar ? '' : 'bg-gray-200 text-gray-600 font-medium'
      }`}>
        {user.avatar ? (
          <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
        ) : (
          getInitials(user.name)
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Users</h1>
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <span className="text-sm sm:text-base text-green-600 font-medium">
            {stats.active.toLocaleString()} Active
          </span>
          <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs sm:text-sm font-medium">
            {stats.pending} Pending
          </span>
          <span className="text-sm sm:text-base text-red-600 font-medium">
            {stats.blocked} Blocked
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 min-w-max">
          <button
            onClick={() => setActiveTab('active')}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'active'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Active Users
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'pending'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span>Pending Approvals</span>
            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
              {stats.pending}
            </span>
          </button>
        </nav>
      </div>

      {activeTab === 'active' ? (
        // Active Users Tab
        <div className="space-y-6">
          {/* Section Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Active Users ({activeUsers.length})
            </h2>
          </div>

          {/* Search and Filters */}
          <div className="space-y-3">
            {/* Search Bar - Full Width on Mobile */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, email, or sport..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Filters - Stack on Mobile, Row on Desktop */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-2 sm:hidden">
                <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm text-gray-600">Filters:</span>
              </div>
              
              <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 sm:gap-3 flex-1">
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="all">All Roles</option>
                  <option value="coach">Coach</option>
                  <option value="player">Player</option>
                  <option value="admin">Admin</option>
                </select>
                
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="blocked">Blocked</option>
                </select>
                
                <select
                  value={sportFilter}
                  onChange={(e) => setSportFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="all">All Sports</option>
                  <option value="tennis">Tennis</option>
                  <option value="football">Football</option>
                  <option value="swimming">Swimming</option>
                  <option value="chess">Chess</option>
                  <option value="basketball">Basketball</option>
                </select>
                
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="recent">Recent</option>
                  <option value="name">Name</option>
                  <option value="dateJoined">Date Joined</option>
                  <option value="lastActive">Last Active</option>
                </select>
              </div>
            </div>
          </div>

          {/* Users Table - Desktop */}
          <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedUsers.length === activeUsers.length}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sport
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Active
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activeUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleUserSelect(user.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <UserAvatar user={user} />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)}`}>
                          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                          {user.sport}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(user.status)}`}>
                          {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.lastActive}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </button>
                          <button className="text-gray-400 hover:text-gray-600">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Users Cards - Mobile & Tablet */}
          <div className="lg:hidden space-y-4">
            {activeUsers.map((user) => (
              <div key={user.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3 flex-1">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleUserSelect(user.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
                    />
                    <UserAvatar user={user} size="md" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 truncate">{user.name}</h3>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                  </div>
                  <button className="text-gray-400 hover:text-gray-600 ml-2">
                    <MoreVertical className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex flex-wrap gap-2 mb-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)}`}>
                    {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                  </span>
                  <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                    {user.sport}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(user.status)}`}>
                    {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                  <span>Last active: {user.lastActive}</span>
                </div>

                <button 
                  onClick={() => navigate(`/admin/users/${user.id}`)}
                  className="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Profile
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        // Pending Approvals Tab
        <div className="space-y-6">
          {/* Section Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Pending User Approvals ({pendingUsers.length})
            </h2>
          </div>

          {/* Pending Users Cards */}
          <div className="space-y-4">
            {pendingUsers.map((user) => (
              <div key={user.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  {/* User Info */}
                  <div className="flex items-start space-x-3 sm:space-x-4 flex-1">
                    <UserAvatar user={user} size="lg" />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900">{user.name}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)}`}>
                          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </span>
                        <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                          {user.sport}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2 break-all">{user.email}</p>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4 flex-shrink-0" />
                          <span>Submitted: {new Date(user.submittedAt).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <FileText className="w-4 h-4 flex-shrink-0" />
                          <span>{user.documentCount} documents</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons - Stack on Mobile */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <button 
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                      className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Details
                    </button>
                    <button
                      onClick={() => handleApproveUser(user.id)}
                      className="inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-gray-900 hover:bg-gray-800"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve
                    </button>
                    <button
                      onClick={() => handleRejectUser(user.id)}
                      className="inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </button>
                  </div>
                </div>

                {/* Documents */}
                <div className="mt-4">
                  <div className="flex flex-wrap gap-2">
                    {user.documents.map((doc, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 text-xs font-medium bg-white border border-gray-200 rounded-full text-gray-700"
                      >
                        {doc}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;